import { Head, Link, useForm } from '@inertiajs/react'
import { ArrowLeft } from 'lucide-react'
import type { ReactNode } from 'react'

import { PageHeader } from '@/components/PageHeader'
import { Button } from '@/components/ui/button'
import { DashboardLayout } from '@/layouts/DashboardLayout'
import { companyInvestmentsPath, companyPath } from '@/lib/routes'

import {
  InvestmentFormFields,
  type InvestmentFormData,
  type Option,
} from './_Form'

type NewInvestment = {
  fund_id: number | null
  date: string
  amount_invested: number | null
  investment_type: InvestmentFormData['investment_type']
  stage_at_investment: string | null
}

type CompanyRef = { id: number; slug: string; name: string }

type Props = {
  company: CompanyRef
  investment: NewInvestment
  funds: Option[]
}

function InvestmentsNew({ company, investment, funds }: Props) {
  const form = useForm<InvestmentFormData>({
    fund_id: investment.fund_id ?? '',
    date: investment.date,
    amount_invested: investment.amount_invested ?? '',
    investment_type: investment.investment_type,
    stage_at_investment: investment.stage_at_investment ?? '',
    terms: { is_post_money: true, mfn: false },
  })

  return (
    <>
      <Head title={`New investment — ${company.name}`} />

      <div className="mx-auto max-w-4xl px-8 py-10">
        <Link
          href={companyPath(company.slug)}
          className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to {company.name}
        </Link>

        <PageHeader
          title="New investment"
          description={`Record a fund check into ${company.name}.`}
        />

        <form
          onSubmit={(event) => {
            event.preventDefault()
            form.transform((data) => ({ investment: data }))
            form.post(companyInvestmentsPath(company.slug))
          }}
        >
          <InvestmentFormFields
            data={form.data}
            errors={form.errors as Record<string, string>}
            setData={form.setData}
            funds={funds}
          />

          <div className="mt-6 flex justify-end gap-2">
            <Link href={companyPath(company.slug)}>
              <Button type="button" variant="ghost">
                Cancel
              </Button>
            </Link>
            <Button type="submit" disabled={form.processing}>
              {form.processing ? 'Saving…' : 'Create investment'}
            </Button>
          </div>
        </form>
      </div>
    </>
  )
}

export default Object.assign(InvestmentsNew, {
  layout: (page: ReactNode) => <DashboardLayout>{page}</DashboardLayout>,
})
